import { Fragment } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { AppRoutes } from '@/constants';
import { getBreadcrumbItems, showAlertSuccess } from '@/utils';

import { ChevronDownIcon, LogoIcon, LogOut01Icon } from '@/assets/icons';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Loading,
  Separator,
} from '@/components/common';

import { useCurrentProfile, useLogout } from '@/features/logins/hooks';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mutateAsync: logout, isPending: isLoggingOut } = useLogout();
  const { data: profile } = useCurrentProfile();

  const breadcrumbItems = getBreadcrumbItems(location.pathname);

  const handleLogout = async () => {
    try {
      await logout();
      showAlertSuccess('Logged out successfully');
      navigate(AppRoutes.LOGIN_PAGE, { replace: true });
    } catch (error) {
      // Error handling is done by the API interceptor
      console.error('Logout failed:', error);
    }
  };

  return (
    <header className="fixed w-full px-8 py-4 bg-white shadow-sm border-neutral-n3 border-width-s border-solid z-[999] h-[68px]">
      <div className="flex items-center justify-between">
        <div className="flex flex-row items-center gap-3">
          <LogoIcon
            onClick={() => {
              navigate(AppRoutes.HOME);
            }}
            className="hover:cursor-pointer"
          />
          <Separator className="h-6 w-[2px]" />
          <Breadcrumb>
            <BreadcrumbList>
              {breadcrumbItems.map((item, index) => (
                <Fragment key={index}>
                  <BreadcrumbItem>
                    {item.isLast ? (
                      <BreadcrumbPage>{item.label}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={item.url}>{item.label}</Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {index < breadcrumbItems.length - 1 && <BreadcrumbSeparator />}
                </Fragment>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex flex-row items-center gap-3 cursor-pointer">
              <span className="text-body-sm font-medium text-brand-primary-darkest">
                {profile?.data.fullName}
              </span>
              <ChevronDownIcon className="w-5 h-5" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            sideOffset={25}
            align="end"
            className="z-[1000] top-[20px] w-[172px]"
          >
            <DropdownMenuItem onClick={handleLogout} className="text-brand-primary-darkest">
              <LogOut01Icon className="text-brand-primary-darkest" /> Log Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {isLoggingOut && <Loading fullScreen />}
    </header>
  );
};

export default Header;
